<template>
  <div v-if="visible">
  <!--
    Bannière du certificat de scolarité.

    Deux états, un seul composant :
      • non configuré → invite à configurer, bouton d'action ;
      • configuré     → rappelle qu'on peut modifier, avec une miniature
                        cliquable du modèle réellement choisi (aperçu vivant,
                        pas une image figée : elle suit la configuration).

    Chaque état a son écran (voir `visible`) : tant que le modèle n'est pas
    configuré, la bannière reste là où l'on se sert du certificat, pour que
    l'invitation tombe sous les yeux ; une fois configuré, elle rejoint les
    paramètres, avec les réglages qu'on ne revisite que rarement.

    Masquée entièrement en mode École : le certificat de scolarité est une
    fonctionnalité du mode collaboratif. Inviter à le configurer là où il ne
    sera jamais proposé n'aurait aucun sens.
  -->
  <div class="cert-banner" :class="configured ? 'is-ready' : 'is-todo'">
    <div class="banner-main">
      <div class="banner-icon">
        <i class="bi" :class="configured ? 'bi-patch-check-fill' : 'bi-file-earmark-text'"></i>
      </div>

      <div class="banner-text">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <h6 class="banner-title mb-0">
            {{ configured ? $t("certificate_banner.ready_title") : $t("certificate_banner.todo_title") }}
          </h6>
          <span class="status-pill">
            {{ configured ? $t("certificate_banner.status_ready") : $t("certificate_banner.status_todo") }}
          </span>
        </div>

        <p class="banner-sub mb-0">
          {{ configured ? $t("certificate_banner.ready_text") : $t("certificate_banner.todo_text") }}
        </p>

        <p class="banner-meta mb-0" v-if="configured">
          <i class="bi bi-layers me-1"></i>
          {{ $t("certificate_banner.current_model") }}
          <strong>{{ $t(`certificate.model_${certificate.template}`) }}</strong>
          <span class="dot-sep">•</span>
          {{ paletteName }}
        </p>
      </div>
    </div>

    <div class="banner-actions">
      <!--
        Miniature 150 × 150 : aperçu réel du modèle. Un clic ouvre la
        visualisation en grand, d'où l'on peut ensuite passer à la
        modification si besoin.
      -->
      <button
        v-if="configured"
        type="button"
        class="thumb"
        :title="$t('certificate_banner.thumb_title')"
        @click="openPreview"
      >
        <div class="thumb-inner">
          <certificate-sheet :data="certificate" />
        </div>
        <span class="thumb-overlay">
          <i class="bi bi-eye"></i>
          <span>{{ $t("certificate_banner.view") }}</span>
        </span>
      </button>

      <!--
        État « non configuré » : c'est le seul point d'entrée vers la
        configuration, puisque la miniature n'existe pas encore.
        Une fois le modèle enregistré, on passe par la miniature, qui ouvre
        la visualisation d'où l'on peut modifier.
      -->
      <button
        v-else
        type="button"
        class="banner-btn"
        @click="goToConfig"
      >
        <i class="bi bi-sliders me-1"></i>
        {{ $t("certificate_banner.configure") }}
      </button>
    </div>
  </div>

  <!--
    Visualisation du modèle, en pleine page.
    Téléportée dans <body> pour ne pas être rognée par la carte qui contient
    la bannière, ni passer sous les autres éléments de la page.
  -->
  <teleport to="body">
    <div v-if="showPreview" class="cert-preview-overlay" @click.self="closePreview">
      <div class="cert-preview-modal">
        <div class="preview-head">
          <div>
            <h6 class="mb-0">{{ $t("certificate_banner.preview_title") }}</h6>
            <span class="preview-meta">
              {{ $t(`certificate.model_${certificate.template}`) }}
              <span class="dot-sep">•</span>
              {{ paletteName }}
            </span>
          </div>
          <button type="button" class="icon-close" @click="closePreview">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="preview-body">
          <certificate-sheet :data="certificate" />
        </div>

        <div class="preview-actions">
          <button type="button" class="btn-ghost" @click="closePreview">
            {{ $t("certificate_banner.close") }}
          </button>
          <button type="button" class="btn-modify" @click="goToConfig">
            <i class="bi bi-pencil-square me-1"></i>
            {{ $t("certificate_banner.modify") }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
  </div>
</template>

<script>
import CertificateSheet from "@/views/Classroom/CertificateSheet.vue";
import {
  classroomSetup,
  isCertificateAvailable,
  isCertificateConfigured,
  loadCertificateFromApi,
} from "@/utils/classroomSetup";
import { paletteOf } from "@/utils/certificateModel";
import { loadSchoolProfile } from "@/utils/schoolProfile";

export default {
  name: "certificate-banner",
  components: { CertificateSheet },
  props: {
    /**
     * Vrai lorsque la bannière est posée sur la page des paramètres.
     * C'est elle qui accueille le modèle une fois configuré ; les écrans de
     * travail ne portent que l'invitation à le configurer.
     */
    settings: { type: Boolean, default: false },
  },
  data() {
    return { setup: classroomSetup, showPreview: false };
  },
  computed: {
    /**
     * La bannière ne s'affiche que sur l'écran qui correspond à son état :
     * l'invitation à configurer là où l'on se sert du certificat, le rappel
     * du modèle enregistré dans les paramètres. Le composant est monté des
     * deux côtés — c'est son montage qui interroge le serveur, et donc qui
     * tranche de quel côté elle apparaît.
     */
    visible() {
      if (!this.available) return false;
      return this.settings ? this.configured : !this.configured;
    },
    /**
     * Paramètres de l'écran d'origine (identifiant de classe, par exemple),
     * à restituer au retour pour retomber sur la même page.
     */
    originParams() {
      const params = this.$route.params || {};
      const kept = {};
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          kept[`p_${key}`] = String(params[key]);
        }
      });
      return kept;
    },
    /** Le module certificat est réservé au mode collaboratif. */
    available() {
      return isCertificateAvailable();
    },
    configured() {
      return isCertificateConfigured();
    },
    certificate() {
      return this.setup.certificate;
    },
    paletteName() {
      const palette = paletteOf(this.certificate.palette);
      return this.$i18n.locale === "fr" ? palette.name_fr : palette.name_en;
    },
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
    // Interroge le serveur : sans cela, un utilisateur ouvrant l'application
    // depuis un autre poste verrait « à configurer » alors que son école a
    // déjà enregistré son modèle.
    if (this.available) {
      loadCertificateFromApi(this.$i18n.locale);
      // Le logo de la miniature vient de la fiche établissement : sans ce
      // chargement, la vignette s'afficherait sans logo.
      loadSchoolProfile();
    }
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    /** Échap ferme l'aperçu, comportement attendu sur ce type de fenêtre. */
    onKeydown(event) {
      if (event.key === "Escape" && this.showPreview) this.closePreview();
    },
    openPreview() {
      this.showPreview = true;
    },
    closePreview() {
      this.showPreview = false;
    },
    goToConfig() {
      this.showPreview = false;
      // On transmet l'écran d'origine : la page de configuration y reviendra
      // après enregistrement, au lieu de renvoyer systématiquement vers la
      // configuration des paiements.
      this.$router.push({
        name: "certificate-config",
        query: { from: this.$route.name, ...this.originParams },
      });
    },
  },
};
</script>

<style scoped>
.cert-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
  flex-wrap: wrap;
  border-radius: 14px;
  padding: 20px 24px;
  margin-bottom: 22px;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Non configuré : accent ambre, invitation à agir. */
.cert-banner.is-todo {
  background: linear-gradient(120deg, rgba(251, 191, 36, 0.14), rgba(251, 191, 36, 0.03) 60%);
  border-color: rgba(251, 191, 36, 0.35);
}

/* Configuré : accent vert, ton de confirmation. */
.cert-banner.is-ready {
  background: linear-gradient(120deg, rgba(46, 206, 191, 0.13), rgba(46, 206, 191, 0.02) 60%);
  border-color: rgba(46, 206, 191, 0.3);
}

.banner-main {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1 1 340px;
  min-width: 0;
}

.banner-icon {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.is-todo .banner-icon {
  background: rgba(251, 191, 36, 0.18);
  color: #fbbf24;
}

.is-ready .banner-icon {
  background: rgba(46, 206, 191, 0.18);
  color: #2ecebf;
}

.banner-title {
  font-size: 15px;
  font-weight: 700;
}

.status-pill {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 2px 9px;
}

.is-todo .status-pill {
  background: rgba(251, 191, 36, 0.18);
  color: #fbbf24;
}

.is-ready .status-pill {
  background: rgba(46, 206, 191, 0.18);
  color: #2ecebf;
}

.banner-sub {
  font-size: 13px;
  opacity: 0.7;
  margin-top: 5px;
  line-height: 1.55;
  max-width: 620px;
}

.banner-meta {
  font-size: 11.5px;
  opacity: 0.6;
  margin-top: 7px;
}

.dot-sep {
  margin: 0 6px;
  opacity: 0.5;
}

.banner-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 0 0 auto;
}

/* --- Miniature 150 × 150 ------------------------------------------------ */

.thumb {
  position: relative;
  width: 104px;
  height: 124px;
  flex: 0 0 auto;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.04);
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.18s ease, transform 0.18s ease;
}

.thumb:hover {
  border-color: #2ecebf;
  transform: translateY(-2px);
}

/*
  La feuille fait 210 × 297 : à 150 px de large elle mesurerait 212 px de haut.
  On la réduit donc de 150/212 ≈ 0.707 pour que la page ENTIÈRE tienne dans
  le carré de 150 × 150 sans être rognée.
*/
.thumb-inner {
  width: 150px;
  transform: scale(0.707);
  transform-origin: center center;
  pointer-events: none;
}

.thumb-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: rgba(9, 14, 28, 0.72);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.18s ease;
}

.thumb-overlay i {
  font-size: 18px;
}

.thumb:hover .thumb-overlay {
  opacity: 1;
}

/* --- Bouton ------------------------------------------------------------- */

.banner-btn {
  border: none;
  border-radius: 9px;
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s ease;
}

.is-todo .banner-btn {
  background: #fbbf24;
  color: #2a1d00;
}

.is-ready .banner-btn {
  background: transparent;
  border: 1px solid rgba(46, 206, 191, 0.6);
  color: #2ecebf;
}

.banner-btn:hover {
  opacity: 0.88;
}

@media (max-width: 767px) {
  .banner-actions {
    width: 100%;
    justify-content: space-between;
  }
}

/* ------------------------------------------------------------------
   Visualisation du modèle (ouverte depuis la miniature)
   ------------------------------------------------------------------ */
.cert-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: rgba(8, 12, 24, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.cert-preview-modal {
  width: 100%;
  max-width: 640px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  background: #141a2f;
  color: #e6ebf5;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  box-shadow: 0 24px 60px -20px rgba(0, 0, 0, 0.7);
  overflow: hidden;
}

.preview-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.preview-meta {
  font-size: 11.5px;
  opacity: 0.6;
}

.icon-close {
  background: transparent;
  border: none;
  color: inherit;
  opacity: 0.6;
  font-size: 14px;
  cursor: pointer;
  padding: 2px 4px;
  line-height: 1;
}

.icon-close:hover {
  opacity: 1;
}

/* La feuille garde ses proportions A4 ; c'est cette zone qui défile si
   la fenêtre est trop courte, pas la page entière. */
.preview-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: rgba(0, 0, 0, 0.18);
}

.preview-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
}

.btn-ghost {
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: transparent;
  color: inherit;
  font-size: 13px;
  padding: 9px 16px;
  cursor: pointer;
}

.btn-ghost:hover {
  border-color: rgba(255, 255, 255, 0.32);
}

.btn-modify {
  border: none;
  border-radius: 9px;
  background: #2ecebf;
  color: #06231f;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 18px;
  cursor: pointer;
}

.btn-modify:hover {
  opacity: 0.9;
}
</style>
